import { inject, injectable } from "inversify";
import * as THREE from "three";
import { Logger, type PointerPosition } from "@designboticteam/core";
import { CursorSnappingService } from "../../../../input/CursorSnappingService";
import { Workspace3DStoreService } from "../../../../services/Workspace3DStoreService";
import { SceneService } from "../../../../stage/SceneService";
import type { ITool } from "../../../../tooling/ITool";
import { Circle } from "../../../entities/Circle/Circle";
import { ShapeEditingService } from "../../../services/ShapeEditingService";
import { ShapeTool } from "../_base/ShapeTool";

const RADIUS_LENGTH_STEP = 1;

@injectable()
class CircleTool extends ShapeTool<Circle> implements ITool {
	public static readonly toolName: string = "CircleTool";
	public readonly toolName: string = CircleTool.toolName;
	protected readonly logger = new Logger(`⭕️ ${this.toolName}`, { color: "#ffffff", backgroundColor: "#4b5563" });

	constructor(
		@inject(SceneService) protected readonly sceneService: SceneService,
		@inject(CursorSnappingService) protected readonly cursorSnappingService: CursorSnappingService,
		@inject(Workspace3DStoreService) protected readonly workspace3DStoreService: Workspace3DStoreService,
		@inject(ShapeEditingService) protected readonly shapeEditingService: ShapeEditingService,
	) {
		super(shapeEditingService);

		this.workspace3DStoreService.store.subscribe(
			(state) => state.tools.dimensionLabelsDisplayMode,
			() => this.applyLabelsVisibility(),
		);

		this.workspace3DStoreService.store.subscribe(
			(state) => state.tools.selectedShape,
			() => this.applyLabelsVisibility(),
		);

		this.logger.log("constructed");
	}

	protected applyLabelsVisibility(): void {
		const { dimensionLabelsDisplayMode, selectedShape } = this.workspace3DStoreService.store.getState().tools;

		for (const shape of this.shapes) {
			if (shape.radiusLine === null) {
				continue;
			}

			const isShapeSelected = selectedShape === shape;
			const isVisible = dimensionLabelsDisplayMode === "all" || (dimensionLabelsDisplayMode === "selected" && isShapeSelected);
			shape.radiusLine.setDimensionLabelVisible({ isVisible });
		}
	}

	public dispose(): void {
		this.discardCurrentShape();
		this.logger.log("disposed");
	}

	public onActivate(): void {
		this.logger.log("activated");
	}

	public onDeactivate(): void {
		this.discardCurrentShape();
	}

	protected override createShape(): Circle {
		return new Circle();
	}

	public onTap(_position: PointerPosition): void {
		const { worldPosition } = this.cursorSnappingService.pickPosition();

		if (worldPosition === null) {
			return;
		}

		if (this.currentShape === null) {
			const shape = this.createShape();
			shape.start({ initialPoint: worldPosition });
			this.shapes.push(shape);
			this.currentShape = shape;
			this.applyLabelsVisibility();
			this.sceneService.scene.add(shape.sceneObject);
			return;
		}

		this.updateShapeRadius({ shape: this.currentShape, radiusTerminalPoint: worldPosition });

		if (this.currentShape.radiusValue >= RADIUS_LENGTH_STEP) {
			this.acceptCurrentShape();
		}
	}

	public onPointerMove(): void {
		if (this.currentShape === null) {
			return;
		}

		const { worldPosition } = this.cursorSnappingService.pickPosition();

		if (worldPosition === null) {
			return;
		}

		this.updateShapeRadius({ shape: this.currentShape, radiusTerminalPoint: worldPosition });
	}

	protected updateShapeRadius({ shape, radiusTerminalPoint }: { shape: Circle; radiusTerminalPoint: THREE.Vector3 }): void {
		if (shape.centerPoint === null) {
			return;
		}

		const xDifference = radiusTerminalPoint.x - shape.centerPoint.x;
		const zDifference = radiusTerminalPoint.z - shape.centerPoint.z;
		const planarDistance = Math.hypot(xDifference, zDifference);
		const quantizedDistance = Math.max(0, Math.round(planarDistance / RADIUS_LENGTH_STEP) * RADIUS_LENGTH_STEP);

		const direction = new THREE.Vector3(xDifference / planarDistance, 0, zDifference / planarDistance);

		shape.updateRadius({ value: quantizedDistance, direction });
	}
}

export { CircleTool };
